import { useState, useEffect, useRef } from "react";
import "../../assets/css/FollowUpLead/FollowUpLead.css";
import { MdClose } from "react-icons/md";
import {
  FaPaperclip,
  FaFileAlt,
  FaFileUpload,
  FaTimes,
  FaTrashAlt,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
} from "react-icons/fa";
import { LEAD_STATUSES } from "../Common/LeadStatus";
import { DEAL_STATUSES } from "../Common/DealStatus";
import Swal from "sweetalert2";
import api from "../../api/axios";
import { validateExpenseAgainstDeal } from "../../utils/expenseValidation";

const EMPTY_USERS_LIST = [];

export default function FollowUpModal({
  id,
  type, // "lead" | "deal"
  isOpen,
  onClose,
  onSuccess,
  usersList = EMPTY_USERS_LIST,
  initialData = null,
  deal = null,
  dealValue = null,
  lead = null,
}) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState(usersList);
  const [isDragging, setIsDragging] = useState(false);
  const [expenseError, setExpenseError] = useState("");
  const [docErrors, setDocErrors] = useState({});
  const [fetchedDeal, setFetchedDeal] = useState(deal || null);
  const [fetchedLead, setFetchedLead] = useState(lead || null);
  const [docEntries, setDocEntries] = useState([
    { id: 1, doc_type: "PO", doc_number: "", doc_file: null }
  ]);
  const [draggedEntryId, setDraggedEntryId] = useState(null);
  const isStatusDirty = useRef(false);
  const isAssigneeDirty = useRef(false);
  const previousAssigneeRef = useRef("");
  const fileInputRef = useRef(null);
  const fileInputRefs = useRef({});

  const getLoggedInUser = () => {
    try {
      const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const getDefaultAssigneeId = (userList = users) => {
    const loggedIn = getLoggedInUser();
    if (!loggedIn) return "";
    const currentId = loggedIn.id ?? loggedIn.user_id;
    if (Array.isArray(userList) && userList.length > 0) {
      const matched = userList.find(
        (u) =>
          (currentId !== undefined && currentId !== null && String(u.id) === String(currentId)) ||
          (loggedIn.email && u.email && String(u.email).toLowerCase() === String(loggedIn.email).toLowerCase()) ||
          (loggedIn.name && u.name && String(u.name).toLowerCase() === String(loggedIn.name).toLowerCase())
      );
      if (matched) return String(matched.id);
    }
    return currentId !== undefined && currentId !== null ? String(currentId) : "";
  };

  const loggedInUser = getLoggedInUser();
  const userRole = String(loggedInUser?.role || "").toLowerCase();
  const isExecutive = userRole.includes("executive");

  const baseStatuses = type === "lead" ? LEAD_STATUSES : DEAL_STATUSES;
  const STATUS_OPTIONS = isExecutive
    ? (type === "lead"
        ? baseStatuses.filter((s) => s !== "Deal Confirmation" && s !== "Lost")
        : baseStatuses.filter((s) => s !== "Won Close" && s !== "Lost"))
    : baseStatuses;

  const [formData, setFormData] = useState({
    remarks: "",
    next_followup_date: "",
    next_followup_time: "",
    status: type === "lead" ? (lead?.status || LEAD_STATUSES[0]) : (deal?.status || DEAL_STATUSES[0]),
    type: "Call",
    assigned_to: getDefaultAssigneeId(usersList),
    actual_expense: 0,
    doc_type: "PO",
    doc_number: "",
    doc_file: null,
  });

  useEffect(() => {
    if (usersList && usersList.length > 0) {
      setUsers(usersList);
      return;
    }

    if (!isOpen) return;

    let isMounted = true;
    api.get?.("/users")
      ?.then((res) => {
        if (isMounted && res?.data && Array.isArray(res.data)) {
          setUsers(res.data);
        }
      })
      ?.catch(() => { });

    return () => {
      isMounted = false;
    };
  }, [isOpen, usersList]);

  // Deal record management
  useEffect(() => {
    if (deal) {
      setFetchedDeal(deal);
      return;
    }
    if (!isOpen || type !== "deal" || !id) {
      setFetchedDeal(null);
      return;
    }

    let isMounted = true;
    api.get?.(`/deal/${id}`)
      ?.then((res) => {
        if (isMounted && res?.data) {
          setFetchedDeal(res.data);
        }
      })
      ?.catch(() => { });

    return () => {
      isMounted = false;
    };
  }, [isOpen, type, id, deal]);

  // Lead record management
  useEffect(() => {
    if (lead) {
      setFetchedLead(lead);
      return;
    }
    if (!isOpen || type !== "lead" || !id) {
      setFetchedLead(null);
      return;
    }

    let isMounted = true;
    api.get?.(`/lead/get/${id}`)
      ?.then((res) => {
        if (isMounted && res?.data) {
          setFetchedLead(res.data);
        }
      })
      ?.catch(() => { });

    return () => {
      isMounted = false;
    };
  }, [isOpen, type, id, lead]);

  // Reset/Initialize form when modal opens or switching records
  useEffect(() => {
    setExpenseError("");
    setDocErrors({});
    isStatusDirty.current = false;
    isAssigneeDirty.current = false;

    if (initialData) {
      const defaultDocType = initialData.status === "Client Invoicing" ? "INVOICE" : "PO";
      let initEntries = [
        {
          id: 1,
          doc_type: defaultDocType,
          doc_number: initialData.doc_number || (initialData.id ? `DOC-${initialData.id}` : ""),
          doc_file: initialData.doc_file || null,
        }
      ];

      if (Array.isArray(initialData.documents) && initialData.documents.length > 0) {
        initEntries = initialData.documents.map((d, index) => ({
          id: d.id || index + 1,
          doc_type: d.doc_type || defaultDocType,
          doc_number: d.doc_number || "",
          doc_file: d.original_filename
            ? { name: d.original_filename, size: d.file_size || 0, isExisting: true, id: d.id }
            : null,
        }));
      }

      setDocEntries(initEntries);

      const initStatus = initialData.status || (type === "lead" ? LEAD_STATUSES[0] : DEAL_STATUSES[0]);
      const loggedIn = getLoggedInUser();
      const loggedInId = loggedIn ? String(loggedIn.id ?? loggedIn.user_id ?? "") : "";
      const isLocked =
        type === "deal" &&
        (initStatus === "Won Close" ||
          initStatus === "Won" ||
          initStatus === "Lost" ||
          initStatus === "Deal Lost");
      const initAssignee = isLocked
        ? (loggedInId || (initialData.assigned_to ? String(initialData.assigned_to) : ""))
        : (initialData.assigned_to ? String(initialData.assigned_to) : "");

      previousAssigneeRef.current = initialData.assigned_to ? String(initialData.assigned_to) : "";

      setFormData({
        remarks: initialData.remarks || "",
        next_followup_date: initialData.next_followup_date
          ? String(initialData.next_followup_date).split("T")[0]
          : "",
        next_followup_time: initialData.next_followup_time || "",
        type: initialData.type || "Call",
        assigned_to: initAssignee,
        status: initStatus,
        actual_expense: initialData.actual_expense || 0,
        doc_type: defaultDocType,
        doc_number: initEntries[0]?.doc_number || "",
        doc_file: initEntries[0]?.doc_file || null,
      });
    } else {
      const currentRecStatus =
        type === "deal"
          ? (deal?.status || fetchedDeal?.status)
          : (lead?.status || fetchedLead?.status);
      const initialStatus = currentRecStatus || (type === "lead" ? LEAD_STATUSES[0] : DEAL_STATUSES[0]);
      const defaultAssignee = getDefaultAssigneeId(usersList?.length ? usersList : users);
      const loggedIn = getLoggedInUser();
      const loggedInId = loggedIn ? String(loggedIn.id ?? loggedIn.user_id ?? "") : "";
      const isLocked =
        type === "deal" &&
        (initialStatus === "Won Close" ||
          initialStatus === "Won" ||
          initialStatus === "Lost" ||
          initialStatus === "Deal Lost");
      const initialAssignee = isLocked ? (loggedInId || defaultAssignee) : defaultAssignee;
      const initialDocType = initialStatus === "Client Invoicing" ? "INVOICE" : "PO";

      previousAssigneeRef.current = defaultAssignee;

      setDocEntries([
        {
          id: 1,
          doc_type: initialDocType,
          doc_number: "",
          doc_file: null,
        }
      ]);

      setFormData({
        remarks: "",
        next_followup_date: "",
        next_followup_time: "",
        type: "Call",
        assigned_to: initialAssignee,
        status: initialStatus,
        actual_expense: 0,
        doc_type: initialDocType,
        doc_number: "",
        doc_file: null,
      });
    }
  }, [id, type, initialData, isOpen]);

  // Handle asynchronously loaded deal or lead status when not manually changed
  useEffect(() => {
    if (initialData || !isOpen) return;
    if (isStatusDirty.current) return;
    const currentRecStatus =
      type === "deal"
        ? (deal?.status || fetchedDeal?.status)
        : (lead?.status || fetchedLead?.status);

    if (currentRecStatus) {
      setFormData((prev) => {
        if (prev.status !== currentRecStatus) {
          const defaultDocType = currentRecStatus === "Client Invoicing" ? "INVOICE" : "PO";
          setDocEntries((prevEntries) =>
            prevEntries.map((entry) => ({
              ...entry,
              doc_type: defaultDocType,
            }))
          );
          const isLockedRec =
            type === "deal" &&
            (currentRecStatus === "Won Close" ||
              currentRecStatus === "Won" ||
              currentRecStatus === "Lost" ||
              currentRecStatus === "Deal Lost");
          const loggedIn = getLoggedInUser();
          const loggedInId = loggedIn ? String(loggedIn.id ?? loggedIn.user_id ?? "") : "";
          const newAssigned = isLockedRec ? (loggedInId || prev.assigned_to) : prev.assigned_to;
          return {
            ...prev,
            status: currentRecStatus,
            doc_type: defaultDocType,
            assigned_to: newAssigned,
          };
        }
        return prev;
      });
    }
  }, [deal?.status, fetchedDeal?.status, lead?.status, fetchedLead?.status, initialData, isOpen, type]);

  // Handle asynchronously loaded users for assigned_to
  useEffect(() => {
    if (initialData || !isOpen) return;
    if (isAssigneeDirty.current) return;
    const defaultAssignee = getDefaultAssigneeId(users);
    if (defaultAssignee) {
      setFormData((prev) => {
        if (!prev.assigned_to) {
          return {
            ...prev,
            assigned_to: defaultAssignee,
          };
        }
        return prev;
      });
    }
  }, [users, initialData, isOpen]);

  const handleAddEntry = () => {
    const defaultDocType = formData.status === "Client Invoicing" ? "INVOICE" : "PO";
    const newEntry = {
      id: Date.now() + Math.random(),
      doc_type: defaultDocType,
      doc_number: "",
      doc_file: null,
    };
    setDocEntries((prev) => [...prev, newEntry]);
  };

  const handleRemoveEntry = (entryId) => {
    if (docEntries.length <= 1) return;
    setDocEntries((prev) => {
      const updated = prev.filter((entry) => entry.id !== entryId);
      if (updated.length > 0) {
        setFormData((f) => ({
          ...f,
          doc_number: updated[0].doc_number || "",
          doc_file: updated[0].doc_file || null,
        }));
      }
      return updated;
    });
    setDocErrors((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  };

  const handleEntryNumberChange = (entryId, val) => {
    setDocErrors((prev) => {
      const next = { ...prev };
      if (next[entryId]) {
        delete next[entryId].doc_number;
      }
      if (entryId === docEntries[0]?.id) {
        delete next.doc_number;
      }
      return next;
    });

    setDocEntries((prev) => {
      const updated = prev.map((entry) =>
        entry.id === entryId ? { ...entry, doc_number: val } : entry
      );
      if (updated[0]?.id === entryId) {
        setFormData((f) => ({ ...f, doc_number: val }));
      }
      return updated;
    });
  };

  const handleEntryFileChange = (entryId, file) => {
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "Maximum allowed file size is 25MB.",
      });
      return;
    }

    setDocErrors((prev) => {
      const next = { ...prev };
      if (next[entryId]) {
        delete next[entryId].doc_file;
      }
      if (entryId === docEntries[0]?.id) {
        delete next.doc_file;
      }
      return next;
    });

    setDocEntries((prev) => {
      const updated = prev.map((entry) =>
        entry.id === entryId ? { ...entry, doc_file: file } : entry
      );
      if (updated[0]?.id === entryId) {
        setFormData((f) => ({ ...f, doc_file: file }));
      }
      return updated;
    });
  };

  const handleEntryFileRemove = (entryId) => {
    setDocEntries((prev) => {
      const updated = prev.map((entry) =>
        entry.id === entryId ? { ...entry, doc_file: null } : entry
      );
      if (updated[0]?.id === entryId) {
        setFormData((f) => ({ ...f, doc_file: null }));
      }
      return updated;
    });
  };

  const handleChange = (e) => {
    let { name, value, files } = e.target;

    if (files) {
      setDocErrors((prev) => ({ ...prev, doc_file: "" }));
      setDocEntries((prev) =>
        prev.map((entry, idx) => (idx === 0 ? { ...entry, doc_file: files[0] } : entry))
      );
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
      return;
    }

    if (name === "doc_type" || name === "doc_number") {
      setDocErrors((prev) => ({ ...prev, [name]: "" }));
      if (name === "doc_number") {
        setDocEntries((prev) =>
          prev.map((entry, idx) => (idx === 0 ? { ...entry, doc_number: value } : entry))
        );
      }
    }

    if (name === "actual_expense") {
      setExpenseError("");
      const rawVal = value.replace(/,/g, "").replace(/\D/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: rawVal,
      }));
      return;
    }

    if (name === "status") {
      isStatusDirty.current = true;
      setExpenseError("");
      setDocErrors({});
      const defaultDocType = value === "Client Invoicing" ? "INVOICE" : "PO";
      setDocEntries((prevEntries) =>
        prevEntries.map((entry) => ({
          ...entry,
          doc_type: defaultDocType,
        }))
      );

      const isLockedStatus =
        type === "deal" &&
        (value === "Won Close" || value === "Won" || value === "Lost" || value === "Deal Lost");
      const loggedIn = getLoggedInUser();
      const loggedInId = loggedIn ? String(loggedIn.id ?? loggedIn.user_id ?? "") : "";

      setFormData((prev) => {
        const wasLocked =
          type === "deal" &&
          (prev.status === "Won Close" ||
            prev.status === "Won" ||
            prev.status === "Lost" ||
            prev.status === "Deal Lost");

        let newAssignedTo = prev.assigned_to;
        if (isLockedStatus) {
          if (!wasLocked) {
            previousAssigneeRef.current = prev.assigned_to;
          }
          newAssignedTo = loggedInId || prev.assigned_to;
        } else if (wasLocked) {
          newAssignedTo =
            previousAssigneeRef.current ||
            getDefaultAssigneeId(usersList?.length ? usersList : users) ||
            prev.assigned_to;
        }

        return {
          ...prev,
          status: value,
          doc_type: defaultDocType,
          assigned_to: newAssignedTo,
        };
      });
      return;
    }

    if (name === "assigned_to") {
      const isLocked =
        type === "deal" &&
        (formData.status === "Won Close" ||
          formData.status === "Won" ||
          formData.status === "Lost" ||
          formData.status === "Deal Lost");
      if (isLocked) return;
      previousAssigneeRef.current = value;
      isAssigneeDirty.current = true;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isEdit = Boolean(initialData?.id);

  const saveFollowup = async () => {
    try {
      setLoading(true);


      if (isDocSectionVisible) {
        const errors = {};
        const newDocErrors = {};
        let hasError = false;

        if (!docEntries || docEntries.length === 0) {
          hasError = true;
          errors.general = "At least one document entry is required.";
        } else {
          docEntries.forEach((entry, index) => {
            const entryErr = {};
            const numLabel = targetDocType === "INVOICE" ? "Invoice Number" : "PO / Doc Number";

            if (!entry.doc_type || !String(entry.doc_type).trim()) {
              entryErr.doc_type = "Document Type is required.";
              hasError = true;
            }
            if (!entry.doc_number || !String(entry.doc_number).trim()) {
              entryErr.doc_number = `${numLabel} is required.`;
              hasError = true;
            }
            if (!entry.doc_file) {
              entryErr.doc_file = "File upload is required.";
              hasError = true;
            }

            if (Object.keys(entryErr).length > 0) {
              newDocErrors[entry.id] = entryErr;
              if (index === 0) {
                Object.assign(errors, entryErr);
              } else {
                if (entryErr.doc_number) {
                  errors[`entry_${index + 1}_number`] = `Entry ${index + 1}: ${entryErr.doc_number}`;
                }
                if (entryErr.doc_file) {
                  errors[`entry_${index + 1}_file`] = `Entry ${index + 1}: ${entryErr.doc_file}`;
                }
              }
            }
          });
        }

        if (hasError) {
          setDocErrors({ ...errors, ...newDocErrors });
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: Object.values(errors).join("\n"),
          });
          setLoading(false);
          return;
        }
      }

      if (type === "deal" && (formData.status === "Won Close" || formData.status === "Won")) {
        let currentDealVal =
          dealValue !== undefined && dealValue !== null
            ? dealValue
            : fetchedDeal?.deal_amount ?? deal?.deal_amount ?? initialData?.deal_amount;

        if (currentDealVal === undefined || currentDealVal === null) {
          try {
            const res = await api.get(`/deal/${id}`);
            currentDealVal = res?.data?.deal_amount ?? res?.data?.actual_amount ?? 0;
            setFetchedDeal(res?.data);
          } catch (e) {
            currentDealVal = 0;
          }
        }

        const validation = validateExpenseAgainstDeal(formData.actual_expense, currentDealVal);
        if (!validation.isValid) {
          setExpenseError(validation.error);
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: validation.error,
          });
          setLoading(false);
          return;
        }
      }

      const isLockedForSave =
        type === "deal" &&
        (formData.status === "Won Close" ||
          formData.status === "Won" ||
          formData.status === "Lost" ||
          formData.status === "Deal Lost");
      const loggedIn = getLoggedInUser();
      const loggedInId = loggedIn ? String(loggedIn.id ?? loggedIn.user_id ?? "") : "";
      const finalAssignedTo = isLockedForSave ? (loggedInId || formData.assigned_to) : formData.assigned_to;

      const payload = {
        remarks: formData.remarks,
        status: formData.status,
        type: formData.type,
        assigned_to: finalAssignedTo ? Number(finalAssignedTo) : null,
        next_followup_date:
          formData.next_followup_date || (isClosed ? new Date().toISOString().split("T")[0] : null),
        next_followup_time:
          formData.next_followup_time || null,
      };

      if (type === "deal") {
        payload.actual_expense =
          formData.status === "Won Close" || formData.status === "Won"
            ? Number(formData.actual_expense) || 0
            : 0;
        payload.lost_reason =
          formData.status === "Lost" || formData.status === "Deal Lost"
            ? formData.remarks || null
            : null;
      }

      if (isDocSectionVisible && (docEntries.some((e) => e.doc_file) || formData.doc_file)) {
        const formDataPayload = new FormData();
        formDataPayload.append("deal_id", String(id));
        formDataPayload.append("remarks", formData.remarks || "");
        formDataPayload.append("status", formData.status || "");
        formDataPayload.append("type", formData.type || "Call");
        if (formData.assigned_to) {
          formDataPayload.append("assigned_to", String(formData.assigned_to));
        }
        if (formData.next_followup_date) {
          formDataPayload.append("next_followup_date", formData.next_followup_date);
        }
        if (formData.next_followup_time) {
          formDataPayload.append("next_followup_time", formData.next_followup_time);
        }
        if (formData.status === "Won Close" || formData.status === "Won") {
          formDataPayload.append("actual_expense", String(formData.actual_expense || 0));
        }
        if (formData.status === "Lost" || formData.status === "Deal Lost") {
          formDataPayload.append("lost_reason", formData.remarks || "");
        }

        // Document attachment & metadata for all entries
        docEntries.forEach((entry) => {
          if (entry.doc_file instanceof File) {
            formDataPayload.append("file", entry.doc_file);
          }
          formDataPayload.append("doc_type", entry.doc_type || targetDocType);
          if (entry.doc_number !== undefined && entry.doc_number !== null && String(entry.doc_number).trim() !== "") {
            formDataPayload.append("doc_number", String(entry.doc_number).trim());
          }
        });

        if (!docEntries.some((e) => e.doc_file instanceof File) && formData.doc_file instanceof File) {
          formDataPayload.append("file", formData.doc_file);
        }

        formDataPayload.append("doc_notes", formData.remarks || "Attached in follow-up");

        if (isEdit) {
          await api.put(`/deal-followup/${initialData.id}`, formDataPayload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          await api.post("/deal-followup/create", formDataPayload, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } else {
        if (isEdit) {
          const UPDATE_API_MAP = {
            lead: `/lead-followup/update/${initialData.id}`,
            deal: `/deal-followup/${initialData.id}`,
          };
          if (type === "lead") {
            payload.lead_id = Number(id) || id;
          } else {
            payload.deal_id = Number(id) || id;
          }
          await api.put(UPDATE_API_MAP[type], payload);
        } else {
          if (type === "lead" && formData.status === "Deal Confirmation") {
            // Defer follow-up creation until deal and document are successfully created
            payload.lead_id = Number(id) || id;
            onSuccess?.({
              status: formData.status,
              isConverted: true,
              isPendingDealConfirmation: true,
              pendingFollowup: payload,
              type,
              id,
            });
            onClose?.();
            return;
          }

          const CREATE_API_MAP = {
            lead: "/lead-followup/create",
            deal: "/deal-followup/create",
          };
          payload[type === "lead" ? "lead_id" : "deal_id"] = Number(id) || id;
          await api.post(CREATE_API_MAP[type], payload);
        }
      }

      await Swal.fire({
        icon: "success",
        title: isEdit ? "Follow Up Updated" : "Follow Up Added",
        timer: 1500,
        showConfirmButton: false,
      });

      const isConvertedStatus =
        formData.status === "Deal Confirmation" ||
        formData.status === "Converted";

      onSuccess?.({
        status: formData.status,
        isConverted: isConvertedStatus,
        type,
        id,
      });
      onClose?.();
    } catch (err) {
      console.error(err);

      let message = `Failed to ${isEdit ? "update" : "create"} follow up`;

      const data = err.response?.data;

      if (typeof data?.detail === "string") {
        message = data.detail;
      }
      else if (Array.isArray(data?.detail)) {
        message = data.detail
          .map((e) => e.msg || JSON.stringify(e))
          .join("\n");
      }
      else if (typeof data?.detail === "object" && data.detail !== null) {
        message = Object.entries(data.detail)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key}: ${value.join(", ")}`;
            }
            return `${key}: ${value}`;
          })
          .join("\n");
      }
      else if (typeof data?.message === "string") {
        message = data.message;
      }
      else if (data && typeof data === "object") {
        message = Object.entries(data)
          .map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key}: ${value.join(", ")}`;
            }
            return `${key}: ${value}`;
          })
          .join("\n");
      }

      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isClosed = ["Won Close", "Won", "Lost", "Deal Lost", "Deal Confirmation", "Converted Lead", "Converted"].includes(formData.status);
  const isDocSectionVisible =
    type === "deal" &&
    (formData.status === "Vendor PO" || formData.status === "Client Invoicing");
  const targetDocType = formData.status === "Client Invoicing" ? "INVOICE" : "PO";
  const isAssignLocked =
    type === "deal" &&
    (formData.status === "Won Close" ||
      formData.status === "Won" ||
      formData.status === "Lost" ||
      formData.status === "Deal Lost");

  const getFileIcon = (file) => {
    if (!file) return <FaFileAlt color="#64748b" />;
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".pdf") || file.type?.includes("pdf")) {
      return <FaFilePdf color="#dc2626" size={18} />;
    }
    if (name.endsWith(".doc") || name.endsWith(".docx") || file.type?.includes("word")) {
      return <FaFileWord color="#2563eb" size={18} />;
    }
    if (name.endsWith(".xls") || name.endsWith(".xlsx") || file.type?.includes("sheet") || file.type?.includes("excel")) {
      return <FaFileExcel color="#16a34a" size={18} />;
    }
    if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || file.type?.startsWith("image/")) {
      return <FaFileImage color="#7c3aed" size={18} />;
    }
    return <FaFileAlt color="#64748b" size={18} />;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setDocErrors((prev) => ({ ...prev, doc_file: "" }));
      setFormData((prev) => ({
        ...prev,
        doc_file: e.dataTransfer.files[0],
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = () => {
    setFormData((prev) => ({
      ...prev,
      doc_file: null,
      doc_number: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="followup-overlay" onClick={onClose}>
      <div
        className="followup-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="followup-header">
          <div className="followup-header-content">
            <h2 className="followup-title">
              {isEdit ? "Edit" : "Add"} {type === "lead" ? "Lead" : "Deal"} Follow Up
            </h2>
            <p className="followup-subtitle">
              Record communication notes or schedule the next interaction
            </p>
          </div>

          <button
            className="followup-close"
            onClick={onClose}
            aria-label="Close"
          >
            <MdClose />
          </button>
        </div>

        {/* BODY */}
        <div className="followup-body">
          {/* ROW 1: FOLLOW UP TYPE & STATUS */}
          <div className="followup-group">
            <label htmlFor="followup_type">Follow Up Type</label>
            <select
              id="followup_type"
              name="type"
              value={formData.type || "Call"}
              onChange={handleChange}
            >
              <option value="Call">Call</option>
              <option value="Email">Email</option>
              <option value="Meeting">Meeting</option>
              <option value="Demo">Demo</option>
            </select>
          </div>

          <div className="followup-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status || ""}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* ROW 2: ASSIGN TO & NEXT FOLLOW UP DATE */}
          <div className="followup-group">
            <label htmlFor="assign_to">Assign To</label>
            <select
              id="assign_to"
              name="assigned_to"
              value={formData.assigned_to || ""}
              onChange={handleChange}
              disabled={isAssignLocked}
              style={isAssignLocked ? { backgroundColor: "#f8fafc", cursor: "not-allowed" } : undefined}
            >
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || `User #${u.id}`}
                </option>
              ))}
              {formData.assigned_to && !users.some((u) => String(u.id) === String(formData.assigned_to)) && (
                <option value={formData.assigned_to}>
                  {getLoggedInUser()?.name || getLoggedInUser()?.email || `User #${formData.assigned_to}`}
                </option>
              )}
            </select>
          </div>

          {!isClosed ? (
            <>
              <div className="followup-group">
                <label htmlFor="next_followup_date">
                  Next Follow Up Date <span className="text-danger">*</span>
                </label>
                <input
                  id="next_followup_date"
                  type="date"
                  name="next_followup_date"
                  aria-label="Next Follow Up Date"
                  value={formData.next_followup_date || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* ROW 3: NEXT FOLLOW UP TIME */}
              <div className="followup-group">
                <label htmlFor="next_followup_time">
                  Next Follow Up Time
                </label>
                <input
                  id="next_followup_time"
                  type="time"
                  name="next_followup_time"
                  value={formData.next_followup_time || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Placeholder space for 2nd column of row 3 */}
              <div className="followup-group" style={{ visibility: "hidden" }} />
            </>
          ) : null}

          {/* ROW 3: ACTUAL EXPENSE (ONLY WHEN WON CLOSE) */}
          {(formData.status === "Won Close" || formData.status === "Won") && (
            <div className="followup-group followup-full">
              <label htmlFor="actual_expense">
                Actual Expense
              </label>
              <input
                id="actual_expense"
                type="text"
                name="actual_expense"
                value={formData.actual_expense ? Number(formData.actual_expense).toLocaleString("en-IN") : ""}
                onChange={handleChange}
                placeholder="e.g. 20,000"
                required
              />
              {expenseError && (
                <span
                  className="error-text"
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {expenseError}
                </span>
              )}
            </div>
          )}

          {/* ROW 4: REMARKS / LOST REASON */}
          <div className="followup-group followup-full">
            {(formData.status === "Lost" || formData.status === "Deal Lost") ? (
              <>
                <label htmlFor="lost_reason">
                  Lost Reason
                </label>
                <input
                  id="lost_reason"
                  type="text"
                  name="remarks"
                  value={formData.remarks || ""}
                  onChange={handleChange}
                  placeholder="Enter reason for lost..."
                />
              </>
            ) : (
              <>
                <label htmlFor="remarks">Remarks</label>
                <textarea
                  id="remarks"
                  name="remarks"
                  rows="3"
                  value={formData.remarks || ""}
                  onChange={handleChange}
                  placeholder="Enter follow up remarks..."
                />
              </>
            )}
          </div>

          {/* ROW 5: PO / INVOICE DOCUMENTS FOR DEALS */}
          {isDocSectionVisible && (
            <div className="followup-doc-section followup-full">
              <div className="followup-doc-header">
                <div className="followup-doc-title">
                  <FaPaperclip />
                  <span>Attach PO / Invoice / Document (Optional)</span>
                </div>
                <div className="followup-doc-header-actions">
                  <button
                    type="button"
                    className="followup-doc-add-btn"
                    onClick={handleAddEntry}
                    title="Add another document entry"
                    aria-label="Add entry"
                  >
                    <span style={{ fontSize: "14px", fontWeight: "bold" }}>+</span>
                    <span>Add {targetDocType === "INVOICE" ? "Invoice" : "PO"}</span>
                  </button>
                </div>
              </div>

              <div className="followup-doc-entries-list">
                {docEntries.map((entry, index) => {
                  const entryDocError = docErrors[entry.id] || (index === 0 ? docErrors : {});
                  const numError = entryDocError.doc_number;
                  const fileError = entryDocError.doc_file;
                  const isFirst = index === 0;

                  return (
                    <div key={entry.id} className="followup-doc-entry-card">
                      <div className="followup-doc-entry-header">
                        <span className="followup-doc-entry-label">
                          Entry {index + 1}: {targetDocType === "INVOICE" ? "Client Invoice" : "Purchase Order (PO)"}
                        </span>
                        <button
                          type="button"
                          className="followup-doc-remove-btn"
                          onClick={() => handleRemoveEntry(entry.id)}
                          disabled={docEntries.length <= 1}
                          title={docEntries.length <= 1 ? "At least one document is required" : `Remove entry ${index + 1}`}
                          aria-label={`Remove entry ${index + 1}`}
                        >
                          −
                        </button>
                      </div>

                      <div className="followup-doc-grid">
                        <div className="followup-group">
                          <label htmlFor={`doc_type_${entry.id}`}>
                            Document Type <span className="text-danger">*</span>
                          </label>
                          <select
                            id={`doc_type_${entry.id}`}
                            name="doc_type"
                            aria-label="Document Type"
                            value={targetDocType}
                            disabled
                            style={{ backgroundColor: "#f8fafc", cursor: "not-allowed" }}
                            required
                          >
                            <option value="PO">Purchase Order (PO)</option>
                            <option value="INVOICE">Client Invoice</option>
                          </select>
                        </div>

                        <div className="followup-group">
                          <label htmlFor={`doc_number_${entry.id}`}>
                            {targetDocType === "INVOICE" ? "Invoice Number" : "PO / Doc Number"}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            id={`doc_number_${entry.id}`}
                            type="text"
                            name="doc_number"
                            aria-label={targetDocType === "INVOICE" ? "Invoice Number" : "PO / Doc Number"}
                            value={entry.doc_number || ""}
                            onChange={(e) => handleEntryNumberChange(entry.id, e.target.value)}
                            placeholder={targetDocType === "INVOICE" ? "e.g. INV-8801" : "e.g. PO-2026-001"}
                            required
                          />
                          {numError && (
                            <span
                              className="error-text"
                              style={{
                                color: "#ef4444",
                                fontSize: "12px",
                                marginTop: "4px",
                                display: "block",
                              }}
                            >
                              {numError}
                            </span>
                          )}
                        </div>

                        <div className="followup-group followup-full">
                          <label htmlFor={`doc_file_${entry.id}`}>
                            Upload File (PDF, Word, Excel, Image) <span className="text-danger">*</span>
                          </label>

                          {!entry.doc_file ? (
                            <div
                              className={`followup-dropzone ${draggedEntryId === entry.id ? "dragging" : ""}`}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDraggedEntryId(entry.id);
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                setDraggedEntryId(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDraggedEntryId(null);
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                  handleEntryFileChange(entry.id, e.dataTransfer.files[0]);
                                }
                              }}
                              onClick={() => {
                                if (isFirst && fileInputRef.current) {
                                  fileInputRef.current.click();
                                } else if (fileInputRefs.current[entry.id]) {
                                  fileInputRefs.current[entry.id].click();
                                }
                              }}
                            >
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[entry.id] = el;
                                  if (isFirst) fileInputRef.current = el;
                                }}
                                id={`doc_file_${entry.id}`}
                                type="file"
                                name="doc_file"
                                aria-label="Upload File (PDF, Word, Excel, Image)"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleEntryFileChange(entry.id, e.target.files[0]);
                                  }
                                }}
                                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
                              />
                              <FaFileUpload size={22} className="followup-dropzone-icon" />
                              <div className="followup-dropzone-text">
                                <strong>Click to browse</strong> or drag & drop file
                              </div>
                              <div className="followup-dropzone-sub">
                                PDF, Word, Excel, Images up to 25MB
                              </div>
                            </div>
                          ) : (
                            <div className="followup-file-selected-card">
                              <div className="followup-file-card-left">
                                <div className="followup-file-icon-box">
                                  {getFileIcon(entry.doc_file)}
                                </div>
                                <div className="followup-file-meta">
                                  <span className="followup-file-name" title={entry.doc_file.name}>
                                    {entry.doc_file.name}
                                  </span>
                                  <span className="followup-file-size">
                                    {entry.doc_file.size
                                      ? `${(entry.doc_file.size / (1024 * 1024)).toFixed(2)} MB`
                                      : "Attached Document"}
                                  </span>
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <button
                                  type="button"
                                  className="followup-file-replace-btn"
                                  onClick={() => {
                                    if (isFirst && fileInputRef.current) {
                                      fileInputRef.current.click();
                                    } else if (fileInputRefs.current[entry.id]) {
                                      fileInputRefs.current[entry.id].click();
                                    }
                                  }}
                                >
                                  Replace
                                </button>
                                <button
                                  type="button"
                                  className="followup-doc-clear-btn"
                                  onClick={() => handleEntryFileRemove(entry.id)}
                                  title="Remove attached file"
                                  aria-label="Remove File"
                                >
                                  <FaTrashAlt size={10} style={{ marginRight: 4 }} />
                                  Remove File
                                </button>
                              </div>
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[entry.id] = el;
                                  if (isFirst) fileInputRef.current = el;
                                }}
                                id={`doc_file_${entry.id}`}
                                type="file"
                                name="doc_file"
                                aria-label="Upload File (PDF, Word, Excel, Image)"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleEntryFileChange(entry.id, e.target.files[0]);
                                  }
                                }}
                                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
                              />
                            </div>
                          )}
                          {fileError && (
                            <span
                              className="error-text"
                              style={{
                                color: "#ef4444",
                                fontSize: "12px",
                                marginTop: "4px",
                                display: "block",
                              }}
                            >
                              {fileError}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="followup-footer">
          <button
            className="followup-btn followup-btn--secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="followup-btn followup-btn--primary"
            onClick={saveFollowup}
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : type === "lead" && formData.status === "Deal Confirmation"
                ? "Next"
                : "Save Follow Up"}
          </button>
        </div>
      </div>
    </div>
  );
}